// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

/**
 * Edition system: which features a deployment offers.
 *
 * - `community` (default): no billing, pricing or usage pages.
 * - `cloud`: Stripe billing, pricing page and usage dashboard.
 *
 * Set with VITE_EDITION=community | cloud. A runtime value injected by
 * docker/runtime-env.sh (`window.__ENV.VITE_EDITION`) wins over the
 * build-time value. Anything other than `cloud` means `community`.
 */

export type Edition = 'community' | 'cloud';

export function resolveEdition(): Edition {
  const runtime =
    typeof window !== 'undefined' ? window.__ENV?.VITE_EDITION : undefined;
  const raw = runtime || import.meta.env.VITE_EDITION || 'community';
  return raw === 'cloud' ? 'cloud' : 'community';
}

/** Resolved once per app lifecycle. */
export const EDITION: Edition = resolveEdition();

export const isCloudEdition = EDITION === 'cloud';

export const isCommunityEdition = EDITION === 'community';

/**
 * Services an operator can hide: from the environment
 * (`VITE_DISABLED_SERVICES`, comma separated) and, at runtime, from the admin
 * settings via `updateDisabledServices`.
 */
function parseList(raw: string | undefined): Set<string> {
  return new Set(
    (raw ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  );
}

function resolveDisabledServices(): Set<string> {
  const runtime =
    typeof window !== 'undefined'
      ? window.__ENV?.VITE_DISABLED_SERVICES
      : undefined;
  // Both sources count: the image always writes the runtime key, often empty.
  return new Set([
    ...parseList(import.meta.env.VITE_DISABLED_SERVICES),
    ...parseList(runtime),
  ]);
}

function resolveEnabledServices(): Set<string> {
  const runtime =
    typeof window !== 'undefined'
      ? window.__ENV?.VITE_ENABLED_SERVICES
      : undefined;
  return new Set([
    ...parseList(import.meta.env.VITE_ENABLED_SERVICES),
    ...parseList(runtime),
  ]);
}

function mergeDisabled(extra: Iterable<string>): Set<string> {
  const merged = new Set<string>(extra);
  for (const key of resolveEnabledServices()) merged.delete(key);
  return merged;
}

let disabledServices: Set<string> = mergeDisabled(resolveDisabledServices());

function isServiceEnabled(key: string): boolean {
  return !disabledServices.has(key);
}

/** Replace the admin-chosen part of the disabled set; effective at once. */
export function updateDisabledServices(keys: string[]): void {
  disabledServices = mergeDisabled([...resolveDisabledServices(), ...keys]);
  serviceListeners.forEach((listener) => listener());
}

const serviceListeners = new Set<() => void>();

/** Called after the admin list changes; returns the unsubscribe function. */
export function subscribeServices(listener: () => void): () => void {
  serviceListeners.add(listener);
  return () => serviceListeners.delete(listener);
}

export function getDisabledServices(): string[] {
  return Array.from(disabledServices);
}

export const SERVICE_KEYS = ['vibeResearch', 'vibeAstock'] as const;

export type ServiceKey = (typeof SERVICE_KEYS)[number];

export const SERVICE_LABELS: Record<ServiceKey, string> = {
  vibeResearch: 'Vibe Research',
  vibeAstock: 'Vibe AStock',
};

/**
 * Feature flags derived from the edition.
 *
 * ```ts
 * import { features } from '@/lib/edition';
 * if (features.billing) { ... }
 * ```
 */
export const features = {
  /** Hosted-account sign-in */
  auth: isCloudEdition,
  /** Stripe billing, pricing page, subscription management */
  billing: isCloudEdition,
  /** Admin LLM provider management panel */
  adminLLM: isCloudEdition,
  /** IM channel integration */
  channels: isCloudEdition,
  /** Usage metering and dashboard */
  usageDashboard: isCloudEdition,
  /** Model routing (cloud provider selection) */
  modelRouting: isCloudEdition,
  /** Gateway / OpenClaw multi-channel bot UI */
  gateway: isCloudEdition,
  /** Admin services configuration panel */
  adminServices: isCloudEdition,
  /** Hanggent's product pages (stock suite, career, car search, ...) */
  products: isCloudEdition,
  /** Server-backed notes: Settings list, chat attachments */
  notes: isCloudEdition,
  /** Slash command suggestions (claude_code mode) */
  slashCommands: isCloudEdition,
  /** Claude Code terminal in the chat box (the server decides whether this person may use it) */
  cliDrawer: isCloudEdition,

  // Configurable services: getters, so an admin update shows at once.
  get vibeResearch() {
    return isCloudEdition && isServiceEnabled('vibeResearch');
  },
  get vibeAstock() {
    return isCloudEdition && isServiceEnabled('vibeAstock');
  },
};
